import React, { useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CardEntry from './CardEntry';
import { postPaymentStatus, recordSubscriptionConsent } from '../functions/paymentApi';

const FREQUENCY_LABELS = { weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' };

const formatChargeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const SubscriptionConsent = ({ amount, cadenceLabel, nextChargeLabel, consented, onChange }) => (
  <div className="mb-6 p-4 rounded-md border border-blue-200 bg-blue-50">
    <h4 className="text-sm font-semibold text-blue-900 mb-2">Recurring payment authorization</h4>
    <dl className="text-sm text-blue-900 space-y-1 mb-3">
      <div className="flex justify-between">
        <dt>Cadence</dt>
        <dd className="font-medium">{cadenceLabel}</dd>
      </div>
      <div className="flex justify-between">
        <dt>Amount</dt>
        <dd className="font-medium">${amount}</dd>
      </div>
      <div className="flex justify-between">
        <dt>Next charge</dt>
        <dd className="font-medium">{nextChargeLabel}</dd>
      </div>
    </dl>
    <label className="flex items-start gap-2 text-sm text-blue-900 cursor-pointer">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={consented}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        I authorize recurring {cadenceLabel.toLowerCase()} charges of ${amount} to the card I provide below,
        until I cancel.
      </span>
    </label>
  </div>
);

const PaymentForm = ({
  clientSecret,
  setupIntentSecret,
  invoiceId,
  invoiceType,
  email,
  name,
  amount,
  stripePk,
  cadence,
  nextChargeDate,
  onSuccess,
}) => {
  // A missing publishable key used to fail silently and look like a UI bug: Stripe
  // never initializes, useStripe() stays null, CardElement never mounts, and the
  // client is left staring at an empty bordered box they cannot click or type into.
  // That is exactly how it reached a client. Surface it instead of rendering a dead
  // field, and never call loadStripe with an empty key.
  const stripeConfigured = Boolean(stripePk);
  const stripePromise = useMemo(
    () => (stripeConfigured ? loadStripe(stripePk) : null),
    [stripeConfigured, stripePk]
  );
  const isSubscription = invoiceType === 'subscription';
  // Subscriptions confirm the SetupIntent FIRST (attaches the card to the customer
  // and lets the webhook set it as the renewal default), then charge the first
  // invoice with that saved card. Confirming the PaymentIntent first and then
  // reusing the same card on a SetupIntent makes Stripe reject an unattached
  // PaymentMethod. Falls back to a plain PaymentIntent charge if no SetupIntent.
  const useSetupFirst = isSubscription && !!setupIntentSecret;
  const displayAmount = typeof amount === 'number' ? amount.toFixed(2) : amount;

  const [consented, setConsented] = useState(!isSubscription);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const cadenceLabel = FREQUENCY_LABELS[cadence] || 'Monthly';
  const nextChargeLabel = formatChargeDate(nextChargeDate) || 'today, then automatically each period after';

  const handleConsentChange = (checked) => {
    setConsented(checked);
    if (checked) {
      recordSubscriptionConsent(invoiceId);
    }
  };

  const finishPayment = async (paymentMethodId) => {
    if (paymentMethodId) {
      try {
        await postPaymentStatus(invoiceId, {
          paymentMethodId,
          status: 'succeeded',
          type: isSubscription ? 'subscription' : 'one-time',
          consent: isSubscription ? { authorizedAt: new Date().toISOString() } : undefined,
        });
      } catch {
        // Best-effort UI sync only — the Stripe webhook remains authoritative (billing-contract.md §2.1).
      }
    }
    setSuccess(true);
    if (onSuccess) {
      setTimeout(() => onSuccess(), 3000);
    } else {
      setTimeout(() => window.location.reload(), 3000);
    }
  };

  const handleCardSuccess = async (result) => {
    setError(null);

    // CardEntry just confirmed the SetupIntent, so this card is now attached to the
    // customer. Charge the first invoice with it. (Reusing an unattached card is
    // what Stripe was rejecting before.)
    if (useSetupFirst && clientSecret) {
      try {
        const stripe = await stripePromise;
        const { error: payError } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: result.paymentMethodId,
        });
        if (payError) {
          setError(
            payError.message ||
              'Your card was saved, but the first charge did not go through. Please contact support.'
          );
          return;
        }
      } catch (err) {
        setError(err.message || 'Your card was saved, but the first charge did not go through.');
        return;
      }
    }

    await finishPayment(result.paymentMethodId);
  };

  const handleCardError = (cardError) => {
    setError(cardError.message || 'Payment failed. Please try again.');
  };

  const primarySecret = useSetupFirst ? setupIntentSecret : clientSecret;
  const primaryMode = useSetupFirst ? 'setup' : 'payment';
  const missingClientSecret = !primarySecret;

  return (
    <div className="payment-container mt-4 sm:mt-8 bg-white rounded-lg p-4 sm:p-6 shadow-md">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
          {isSubscription ? 'Set Up Subscription' : 'Pay Invoice'}
        </h3>
        <div className="mt-1">
          <span className="text-xl sm:text-2xl font-bold text-gray-900">${displayAmount}</span>
        </div>
      </div>

      {isSubscription && !success && (
        <SubscriptionConsent
          amount={displayAmount}
          cadenceLabel={cadenceLabel}
          nextChargeLabel={nextChargeLabel}
          consented={consented}
          onChange={handleConsentChange}
        />
      )}

      {!stripeConfigured && !success && (
        <div className="mb-4 text-sm text-red-600" role="alert">
          Card payments are unavailable right now. Please contact the sender to pay
          another way.
        </div>
      )}

      {stripeConfigured && missingClientSecret && !success && (
        <div className="mb-4 text-sm text-red-600" role="alert">
          Payment could not be prepared for this invoice. Please contact the sender.
        </div>
      )}

      {!success && stripeConfigured && !missingClientSecret && (
        <Elements stripe={stripePromise}>
          <CardEntry
            clientSecret={primarySecret}
            mode={primaryMode}
            submitLabel={`Pay $${displayAmount}`}
            disabled={!consented}
            billingDetails={{ name, email }}
            onSuccess={handleCardSuccess}
            onError={handleCardError}
            onProcessingChange={setIsProcessing}
          />
        </Elements>
      )}

      {error && (
        <div className="mt-4 text-sm text-red-600" role="alert">
          {error}
        </div>
      )}

      <div className="mt-4 text-sm text-gray-500 flex items-center">
        <svg className="h-5 w-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
        </svg>
        Secured by Stripe
      </div>

      {isProcessing && !success && (
        <div className="flex flex-col items-center justify-center p-8">
          <svg className="animate-spin h-10 w-10 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <p className="text-lg text-gray-700">
            {isSubscription ? 'Setting up subscription...' : 'Processing your payment...'}
          </p>
          <p className="text-sm text-gray-500 mt-2">Please do not close this window.</p>
        </div>
      )}

      {success && (
        <div className="p-4 rounded-md bg-green-50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Payment Successful!</h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  {isSubscription
                    ? 'Thank you. Your subscription is set up and your card will be charged automatically each period.'
                    : 'Thank you for your payment. Your invoice has been updated.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentForm;
